/**
 * Sidecar marker proving which `.credentials.json` Conveyor wrote.
 *
 * Ownership used to be inferred from the ABSENCE of a refreshToken — our
 * synthesized file had none, a real interactive `/login` always did. A
 * `claude_oauth` key (in-app sign-in) makes Conveyor hold refreshable
 * credentials too, so that signal would fail OPEN and start overwriting real
 * logins. This marker replaces it; see credentials.ts for the policy that
 * consumes it.
 */
import { createHash } from "node:crypto";
import { join } from "node:path";
import { claudeConfigHome } from "./settings.js";

/** Sidecar recording which access token Conveyor last wrote. */
export function conveyorCredentialsMarkerPath(): string {
  return join(claudeConfigHome(), ".conveyor-credentials.json");
}

/** Hash, never the token itself — the marker sits beside the credential. */
export function tokenFingerprint(accessToken: string): string {
  return createHash("sha256").update(accessToken).digest("hex");
}

/**
 * The marker carries the PREVIOUS fingerprint alongside the new one so both
 * count as ours. Without it, a credential write that fails its read-back (the
 * GCS-FUSE case writeWithReadBackRetry exists for) would leave a marker naming
 * a token that never landed: the file still on disk would stop matching, and
 * both the write path and removeConveyorCredentials would call it foreign
 * forever, permanently stranding the pod's own credential.
 */
export function buildCredentialsMarker(
  accessToken: string,
  now: number,
  previousHash?: string | null,
): string {
  const accessTokenSha256 = tokenFingerprint(accessToken);
  return JSON.stringify({
    accessTokenSha256,
    ...(previousHash && previousHash !== accessTokenSha256
      ? { previousAccessTokenSha256: previousHash }
      : {}),
    writtenAt: now,
  });
}

/** Every fingerprint the marker vouches for, newest first. */
export function parseCredentialsMarker(raw: string | null): string[] {
  if (!raw || raw.trim() === "") return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return [];
    const record = parsed as Record<string, unknown>;
    return [record.accessTokenSha256, record.previousAccessTokenSha256].filter(
      (hash): hash is string => typeof hash === "string" && hash.length > 0,
    );
  } catch {
    return [];
  }
}
